import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, Calendar, Users, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const PromotionsManagement = () => {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [filteredPromotions, setFilteredPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    theme: '',
    start_timestamp: '',
    end_timestamp: '',
    discount_mandate_min: ''
  });

  useEffect(() => {
    fetchPromotions();
  }, []);

  useEffect(() => {
    filterPromotions();
  }, [activeTab, promotions]);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/marketing/promotions');
      setPromotions(response.data || []);
    } catch (error) {
      toast.error('Failed to load promotions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterPromotions = () => {
    if (activeTab === 'all') {
      setFilteredPromotions(promotions);
    } else {
      setFilteredPromotions(promotions.filter(p => p.status.toLowerCase() === activeTab));
    }
  };

  const handleOpenModal = (promotion = null) => {
    if (promotion) {
      setEditingPromotion(promotion);
      const startDate = new Date(promotion.start_timestamp);
      const endDate = new Date(promotion.end_timestamp);
      setFormData({
        name: promotion.name,
        theme: promotion.theme,
        start_timestamp: startDate.toISOString().slice(0, 16),
        end_timestamp: endDate.toISOString().slice(0, 16),
        discount_mandate_min: promotion.discount_mandate_min
      });
    } else {
      setEditingPromotion(null);
      setFormData({
        name: '',
        theme: '',
        start_timestamp: '',
        end_timestamp: '',
        discount_mandate_min: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      start_timestamp: new Date(formData.start_timestamp).toISOString(),
      end_timestamp: new Date(formData.end_timestamp).toISOString(),
      discount_mandate_min: parseFloat(formData.discount_mandate_min)
    };

    try {
      if (editingPromotion) {
        await apiCall(`/api/marketing/promotions/${editingPromotion.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        toast.success('Promotion updated successfully');
      } else {
        await apiCall('/api/marketing/promotions', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        toast.success('Promotion created successfully');
      }
      
      setIsModalOpen(false);
      fetchPromotions();
    } catch (error) {
      toast.error(error.message || 'Failed to save promotion');
    }
  };

  const handleDelete = async (promotionId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    
    try {
      await apiCall(`/api/marketing/promotions/${promotionId}`, {
        method: 'DELETE'
      });
      toast.success('Promotion deleted successfully');
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to delete promotion');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Live':
        return 'bg-green-100 text-green-800';
      case 'Upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'Expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const stats = {
    total: promotions.length,
    upcoming: promotions.filter(p => p.status === 'Upcoming').length,
    live: promotions.filter(p => p.status === 'Live').length,
    expired: promotions.filter(p => p.status === 'Expired').length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Promotions & Sales</h1>
            <p className="text-gray-600 mt-2">Manage platform-wide promotional campaigns</p>
          </div>
          <Button onClick={() => handleOpenModal()}>
            <Plus className="h-4 w-4 mr-2" />
            Create Promotion
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Promotions</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Upcoming</p>
                  <p className="text-2xl font-bold">{stats.upcoming}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Live</p>
                  <p className="text-2xl font-bold">{stats.live}</p>
                </div>
                <Calendar className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Expired</p>
                  <p className="text-2xl font-bold">{stats.expired}</p>
                </div>
                <Calendar className="h-8 w-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card>
          <CardHeader>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
                <TabsTrigger value="upcoming">Upcoming ({stats.upcoming})</TabsTrigger>
                <TabsTrigger value="live">Live ({stats.live})</TabsTrigger>
                <TabsTrigger value="expired">Expired ({stats.expired})</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-12 text-gray-500">Loading...</p>
            ) : filteredPromotions.length === 0 ? (
              <p className="text-center py-12 text-gray-500">No promotions found</p>
            ) : (
              <div className="space-y-4">
                {filteredPromotions.map((promotion) => (
                  <Card key={promotion.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold">{promotion.name}</h3>
                            <Badge className={getStatusColor(promotion.status)}>
                              {promotion.status}
                            </Badge>
                            <Badge variant="outline">{promotion.theme}</Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="h-4 w-4" />
                              <div>
                                <p className="font-medium">Start Date</p>
                                <p>{formatDate(promotion.start_timestamp)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="h-4 w-4" />
                              <div>
                                <p className="font-medium">End Date</p>
                                <p>{formatDate(promotion.end_timestamp)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Package className="h-4 w-4" />
                              <div>
                                <p className="font-medium">Min Discount</p>
                                <p>{promotion.discount_mandate_min}%</p>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                            <div className="flex items-center gap-2 text-sm">
                              <Users className="h-4 w-4 text-blue-600" />
                              <div>
                                <p className="text-gray-600">Vendors Opted In</p>
                                <p className="font-semibold">{promotion.total_vendors_opted_in || 0}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Package className="h-4 w-4 text-green-600" />
                              <div>
                                <p className="text-gray-600">Products Committed</p>
                                <p className="font-semibold">{promotion.total_products_committed || 0}</p>
                              </div>
                            </div>
                            <div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/marketing/promotions/${promotion.id}/opt-ins`)}
                              >
                                View Opt-ins
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 ml-4">
                          <Button variant="outline" size="sm" onClick={() => handleOpenModal(promotion)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(promotion.id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingPromotion ? 'Edit Promotion' : 'Create Promotion'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Promotion Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Aniyor Diwali Flash Sale 2025"
                  required
                />
              </div>

              <div>
                <Label>Theme *</Label>
                <Input
                  value={formData.theme}
                  onChange={(e) => setFormData({...formData, theme: e.target.value})}
                  placeholder="e.g., Festive, Seasonal, Clearance"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_timestamp}
                    onChange={(e) => setFormData({...formData, start_timestamp: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>End Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_timestamp}
                    onChange={(e) => setFormData({...formData, end_timestamp: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Minimum Discount Required (%) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.discount_mandate_min}
                  onChange={(e) => setFormData({...formData, discount_mandate_min: e.target.value})}
                  placeholder="e.g., 15.0"
                  min="0"
                  max="100"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Vendors must offer at least this discount to participate</p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPromotion ? 'Update' : 'Create'} Promotion
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PromotionsManagement;
